import React, {useCallback, useEffect, useState} from 'react';
import {Button, Spinner} from 'react-bootstrap';
import {doc, getDoc, onSnapshot, setDoc} from 'firebase/firestore';
import {db} from '../../../../shared/services/firebase';
import RefreshIcon from '@mui/icons-material/RefreshRounded';
import moment from 'moment';
import PropTypes from 'prop-types';

const parseTimestamp = (ts) => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  return new Date(ts);
};

const ONE_HOUR_MS = 60 * 60 * 1000;

const EdgeVersionInfo = ({eid}) => {
  const [balenaStatus, setBalenaStatus] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefreshDone, setAutoRefreshDone] = useState(false);

  const extractStatus = (data) => ({
    uuid: data?.balenaUuid || null,
    isOnline: data?.status?.balena?.isOnline ?? false,
    lastConnected: parseTimestamp(data?.status?.balena?.lastConnected),
    releaseVersion: data?.status?.balena?.releaseVersion || null,
    osVersion: data?.status?.balena?.osVersion || null,
    supervisorVersion: data?.status?.balena?.supervisorVersion || null,
    lastUpdate: parseTimestamp(data?.status?.balena?.lastUpdate),
  });

  const triggerRefresh = useCallback(async (balenaUuid) => {
    if (!balenaUuid) return;

    setRefreshing(true);
    await setDoc(doc(db, 'balenaUpdate', eid), {tStamp: new Date()});

    const unsub = onSnapshot(doc(db, 'balenaUpdate', eid), async (d) => {
      if (!d.exists()) {
        const updatedDoc = await getDoc(doc(db, 'edges', eid));
        if (updatedDoc.exists()) {
          setBalenaStatus(extractStatus(updatedDoc.data()));
        }
        setRefreshing(false);
        unsub();
      }
    }, () => {
      setRefreshing(false);
      unsub();
    });
  }, [eid]);

  useEffect(() => {
    if (!eid) return;
    const unsub = onSnapshot(doc(db, 'edges', eid), (snap) => {
      if (snap.exists()) {
        const status = extractStatus(snap.data());
        setBalenaStatus(status);
        if (!autoRefreshDone) {
          setAutoRefreshDone(true);
          const isEmpty = !status.releaseVersion;
          const isStale = status.lastUpdate && (Date.now() - status.lastUpdate.getTime() > ONE_HOUR_MS);
          if (isEmpty || isStale) {
            triggerRefresh(status.uuid);
          }
        }
      }
    });
    return () => unsub();
  }, [eid, autoRefreshDone, triggerRefresh]);

  const handleRefresh = () => triggerRefresh(balenaStatus.uuid);

  const formatDate = (date) => {
    if (!date) return '—';
    return date.toLocaleString(undefined, {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  };

  const formatCloudlinkSince = () => {
    const date = balenaStatus.lastConnected;
    if (!date) return '—';
    return moment(date).fromNow();
  };

  return (
    <div className="island p-3 h-100 d-flex justify-content-between align-items-start gap-3">
      <div className="d-flex flex-column gap-2">
        <div>
          <div className="small mb-1" style={{color: 'var(--text-secondary)'}}>UUID</div>
          <div style={{color: 'var(--text-primary)', fontWeight: 500, fontFamily: 'monospace', fontSize: '0.8rem'}}>
            {balenaStatus.uuid || '—'}
          </div>
        </div>
        <div>
          <div className="small mb-1" style={{color: 'var(--text-secondary)'}}>Release</div>
          <div style={{color: 'var(--text-primary)', fontWeight: 500, fontFamily: 'monospace'}}>
            {balenaStatus.releaseVersion || '—'}
          </div>
        </div>
        <div>
          <div className="small mb-1" style={{color: 'var(--text-secondary)'}}>OS</div>
          <div style={{color: 'var(--text-primary)', fontWeight: 500, fontFamily: 'monospace'}}>
            {balenaStatus.osVersion || '—'}
          </div>
        </div>
        <div>
          <div className="small mb-1" style={{color: 'var(--text-secondary)'}}>Supervisor</div>
          <div style={{color: 'var(--text-primary)', fontWeight: 500, fontFamily: 'monospace'}}>
            {balenaStatus.supervisorVersion || '—'}
          </div>
        </div>
        <div>
          <div className="small mb-1" style={{color: 'var(--text-secondary)'}}>Balena Status</div>
          <div className="d-flex align-items-center gap-2">
            <span
              className="rounded-circle d-inline-block"
              style={{
                width: 8, height: 8,
                backgroundColor: balenaStatus.isOnline ? '#28a745' : '#adb5bd',
                flexShrink: 0,
              }}
            />
            <span style={{color: 'var(--text-primary)', fontWeight: 500}}>
              {balenaStatus.isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>
        <div>
          <div className="small mb-1" style={{color: 'var(--text-secondary)'}}>Cloudlink {balenaStatus.isOnline ? 'Connected' : 'Disconnected'} Since</div>
          <div style={{color: 'var(--text-primary)', fontWeight: 500}}>
            {formatCloudlinkSince()}
          </div>
        </div>
      </div>
      <div className="d-flex align-items-center gap-2" style={{flexShrink: 0}}>
        <span className="small" style={{color: 'var(--text-secondary)'}}>
          {formatDate(balenaStatus.lastUpdate)}
        </span>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing || !balenaStatus.uuid}
          title="Refresh version info"
          style={{borderColor: 'var(--border-color)', color: 'var(--text-secondary)', padding: '1px 6px'}}
        >
          {refreshing ? (
            <Spinner animation="border" size="sm"/>
          ) : (
            <RefreshIcon style={{fontSize: 16}}/>
          )}
        </Button>
      </div>
    </div>
  );
};

EdgeVersionInfo.propTypes = {
  eid: PropTypes.string.isRequired,
};

export default EdgeVersionInfo;
